import { useMemo } from "react";

import useDebouncedValue from "../util/useDebouncedValue";

// Debounce rapid moment navigation by 500ms: server-side, agent state at a
// moment is recomputed by replaying the session, so every distinct moment
// fetched is expensive (INFRA-152).
export const MOMENT_DEBOUNCE_MS = 500;

/**
 * The (momentId, sessionId) pair the session viewer's data queries fetch.
 *
 * Debounced as one value so rapid navigation coalesces into a single set of
 * fetches for the moment the user settles on, and so clone navigation — which
 * changes both — never exposes a mismatched (session, moment) pair.
 *
 * The debounce has an immediate leading edge: an isolated selection (including
 * the automatic latest-moment selection on load) fetches right away. Only a
 * selection made within MOMENT_DEBOUNCE_MS of the previous selection waits,
 * and every selection restarts the window whether or not it was itself
 * debounced — see useDebouncedValue.
 */
const useSettledMomentTarget = (
  momentId: number | null,
  activeSessionId: string
): { momentId: number | null; sessionId: string } =>
  useDebouncedValue(
    useMemo(
      () => ({ momentId, sessionId: activeSessionId }),
      [momentId, activeSessionId]
    ),
    MOMENT_DEBOUNCE_MS
  );

export default useSettledMomentTarget;
